import { api, direRefus } from '../api.ts';
import { $, h, instant, jour, mot, peindre, valeur, type Brut } from '../ui.ts';
import { peutEcrire, estAdmin, type Session } from '../etat.ts';
import { condition, equivalentJour, type LigneProfil } from './vivier.ts';
import { rendreMarkdown } from '../markdown.ts';
import { ETAPES_CHAINE, NIVEAUX_AXE, NIVEAUX_IA, NIVEAU_MAX, POSTURES_IA,
         TERRAINS_IA, niveauAxe, type Axe } from '../../domaine/ia.ts';

interface Document {
  id: string; nom: string; categorie: string; mime: string; taille: number;
  depose_le: string; depose_par: string | null; consultations: number; telechargements: number;
  /** Déposé par quelqu'un, ou produit par MANA. */
  origine?: 'depose' | 'genere';
}
interface Note { id: string; texte: string; visibilite: string; cree_le: string; auteur: string }
interface Vise { appui: string; secteur?: string; direction?: string }
interface Synthese {
  positionnement: string; prouve: { fait: string; source: string }[];
  vigilance: string[]; ouvertes: string[];
  secteurs_cibles?: Vise[]; directions_cibles?: Vise[];
  ia_niveau?: number; ia_depuis?: number; ia_justification?: string;
}
/** L'état du CV WAKEA conservé, tel que la fiche le renvoie. */
interface CvWakea {
  document: string | null;
  produit_le: string | null;
  /** Le document conservé décrit-il encore la fiche telle qu'elle est ? */
  a_jour: boolean;
  /** Ce qui manque pour en composer un. Vient du serveur, qui refuse pour de bon. */
  manques: string[];
}
interface Dossier {
  profil: LigneProfil & Record<string, unknown>;
  documents: Document[]; notes: Note[];
  experiences: { id: string; intitule: string; client: string | null; resume: string }[];
  cv_wakea: CvWakea;
}

/** Ce qu'on peut déposer. `cv_wakea` n'y est pas : il n'est pas déposé, il est produit. */
const CATEGORIES: Record<string, string> = {
  cv: 'CV', entretien: 'Entretien', reference: 'Référence', contrat: 'Contrat', autre: 'Autre',
};

/** Ce qu'on peut lire au dossier, ce qui est déposé comme ce qui est produit. */
const LIBELLES: Record<string, string> = { ...CATEGORIES, cv_wakea: 'CV WAKEA' };

/**
 * L'état de conformité de ce dossier-ci. Il est contextualisé — régime applicable,
 * échéance, preuve — parce qu'un encadré qui répéterait la règle générale à chaque
 * ouverture de fiche ne serait lu par personne, et ne dirait rien de ce dossier.
 */
const conformite = (p: Record<string, unknown>, id: string, ecriture: boolean) => {
  const repere = p['collecte'] !== 'directe';
  const informe = p['informe_le'] as string | null;
  const accusee = p['notice_accusee_le'] as string | null;
  const opposition = p['opposition_le'] as string | null;

  if (opposition) return h`<div class="enc ar"><span class="sur">OPPOSITION REÇUE</span>
    <p>Cette personne s'est opposée à la conservation de son dossier le ${jour(opposition)}. Il a
       quitté le vivier. L'effacement définitif passe par la purge, en console.</p></div>`;

  // Elle s'était opposée, puis elle est revenue d'elle-même. Le dossier est légitime, et
  // le dire protège tout le monde : personne ne relance au nom du cabinet quelqu'un qui a
  // déjà demandé à partir une fois sans le savoir.
  const revenue = p['opposition_anterieure_le'] as string | null;

  return h`${revenue ? h`<div class="enc ar" style="margin-bottom:12px">
      <span class="sur">S'ÉTAIT OPPOSÉE LE ${jour(revenue)}</span>
      <p>Cette personne avait demandé que son dossier ne soit pas conservé, puis elle a
         candidaté de nouveau <b>d'elle-même</b> depuis le site. Le dossier est donc légitime.
         Restez-en à ce qu'elle a demandé cette fois-ci, et ne la réinscrivez jamais de votre
         propre initiative : la liste de suppression s'y oppose.</p></div>` : ''}
  <div class="enc ${informe ? 'gl' : 'ar'}">
    <span class="sur">INFORMATION DE LA PERSONNE</span>
    <p>${repere
        ? h`Profil repéré par le cabinet${p['origine'] ? ` sur ${String(p['origine'])}` : ''} — la notice
            est due dans le mois qui suit l'enregistrement, même sans prise de contact.`
        : h`La personne s'est manifestée${p['origine'] ? ` par ${String(p['origine'])}` : ''} — la notice
            est due dès l'enregistrement.`}</p>
    <p style="margin-top:7px">${informe
      ? h`Notice envoyée le ${jour(informe)}${p['notice_version'] ? h` · version ${String(p['notice_version'])}` : ''}.
          ${accusee ? h`<b>Réception accusée le ${jour(accusee)}.</b>` : 'Réception non accusée à ce jour.'}`
      : h`<b>Notice non envoyée.</b> ${p['email'] ? 'Ce dossier est en défaut.' : "Aucune adresse au dossier : renseignez-la, puis envoyez."}`}</p>
    ${ecriture ? h`<div class="rang" style="margin-top:9px">
      <button class="bt s" data-act="notice" data-id="${id}">${informe ? 'Renvoyer la notice' : 'Envoyer la notice'}</button></div>` : ''}
  </div>`;
};

/**
 * Les trois façons de joindre quelqu'un, en un clic chacune. Sur un téléphone, `tel:`
 * lance l'appel ; sur un ordinateur, il passe au logiciel de téléphonie. Un numéro qu'on
 * doit recopier à la main est un numéro qu'on ne compose pas.
 */
const coordonnees = (p: Record<string, unknown>) => {
  const email = p['email'] as string | null;
  const tel = p['telephone'] as string | null;
  const linkedin = p['linkedin'] as string | null;
  if (!email && !tel && !linkedin) return h`<p class="pt sourd" style="margin:7px 0 0">Aucune coordonnée au dossier.</p>`;
  return h`<div class="rang" style="flex-wrap:wrap;gap:16px;margin-top:7px">
    ${email ? h`<a class="sm lien-c" href="mailto:${email}">${email}</a>` : ''}
    ${tel ? h`<a class="sm lien-c" href="tel:${tel.replace(/[^+\d]/g, '')}">${tel}</a>` : ''}
    ${linkedin ? h`<a class="sm lien-c" href="${linkedin}" target="_blank" rel="noopener noreferrer">LinkedIn</a>` : ''}
  </div>`;
};

/**
 * Où positionner cette personne. Les secteurs et les directions se jugent sur le parcours,
 * **indépendamment de l'IA** : connaître la grande distribution ou savoir comment se décide
 * un budget de DSI s'acquiert sur le terrain, et c'est souvent ce qui manque le plus à une
 * mission. Chaque ligne porte ce qui l'appuie — sans appui, elle ne vaudrait rien.
 */
const positionnement = (s: Synthese | null) => {
  const secteurs = s?.secteurs_cibles ?? [];
  const directions = s?.directions_cibles ?? [];
  if (!secteurs.length && !directions.length) return '';
  const lignes = (items: Vise[], cle: 'secteur' | 'direction') => items.map((x) => h`
    <div style="margin-top:7px">
      <div style="font-size:12.5px;color:var(--obsidian)">${x[cle] ?? ''}</div>
      <div class="pt sourd" style="line-height:1.5">${x.appui}</div></div>`);
  return h`<section>
    <div class="filet-l"><span class="sur">OÙ LE POSITIONNER</span></div>
    <p class="pt sourd" style="margin:8px 0 0">Jugé sur le parcours, et non sur l'IA.</p>
    ${secteurs.length ? h`<div style="margin-top:10px">
      <div class="pt sourd" style="letter-spacing:1.2px">SECTEURS</div>
      ${lignes(secteurs, 'secteur')}</div>` : ''}
    ${directions.length ? h`<div style="margin-top:12px">
      <div class="pt sourd" style="letter-spacing:1.2px">DIRECTIONS MÉTIERS</div>
      ${lignes(directions, 'direction')}</div>` : ''}
  </section>`;
};

/**
 * Un cran sur un axe, de 0 à 3. Des pavés, pas une barre : on compte quatre positions
 * d'un coup d'œil, là où une longueur demande de la comparer à autre chose. Le vide se
 * voit autant que le plein — c'est exactement ce qu'on veut dire d'un dossier mince.
 */
const crans = (n: number) => h`<span class="crans" aria-hidden="true">${
  Array.from({ length: NIVEAU_MAX }, (_, i) => h`<i class="${i < n ? 'plein' : ''}"></i>`)}</span>`;

/**
 * Les mêmes pavés, mais réglables : un clic pose le cran, un second clic sur le cran
 * courant le retire. C'est la règle 8 appliquée au grain le plus fin — le modèle propose
 * une cartographie, le consultant qui a passé une heure avec la personne corrige une case
 * sans refaire la synthèse, donc sans rien repayer et sans rien réécrire d'autre.
 *
 * Deux clics pour effacer, et non un bouton « remettre à zéro » : le zéro dit « non
 * constaté », il se pose aussi souvent qu'il se retire, et mériter un bouton à lui seul
 * l'aurait fait lire comme un jugement.
 */
const cransReglables = (axe: 'ia_chaine' | 'ia_terrains', cle: string, n: number, id: string) => h`
  <span class="crans reglable" data-axe="${axe}" data-cle="${cle}"
    role="group" aria-label="Niveau constaté, de 0 à ${NIVEAU_MAX}">
    ${Array.from({ length: NIVEAU_MAX }, (_, i) => h`<button type="button" class="cran"
      data-cran="${i + 1}" data-axe="${axe}" data-cle="${cle}" data-id="${id}"
      aria-pressed="${i < n}"
      title="${NIVEAUX_AXE[i + 1]}${i + 1 === n ? ' — cliquer de nouveau pour effacer' : ''}"
      ><i class="${i < n ? 'plein' : ''}"></i></button>`)}
  </span>`;

const pavesAxe = (axe: 'ia_chaine' | 'ia_terrains', cle: string, n: number,
                  id: string, ecriture: boolean) =>
  ecriture ? cransReglables(axe, cle, n, id) : crans(n);

/**
 * La chaîne est une séquence — « fort jusqu'à la mise en production, rien sur
 * l'exploitation » est la phrase qu'un consultant dirait au téléphone, et c'est celle que
 * ce bloc doit rendre en une seconde.
 *
 * Empilée, et non en bande horizontale : cinq libellés dont « Industrialisation » et
 * « Preuve de concept » ne tiennent pas côte à côte dans une colonne de 350 px, et se
 * cassaient sur deux ou trois lignes de hauteurs inégales — la séquence devenait illisible
 * au moment même où elle devait se lire d'un coup. De haut en bas, un fil relie les
 * étapes, chaque étape porte son cran **et son libellé de niveau en clair**, et le
 * compteur du titre dit tout de suite si l'axe est renseigné.
 */
const chaineIA = (axe: Axe | null, id: string, ecriture: boolean) => {
  const etapes = ETAPES_CHAINE.map((e) => ({ ...e, n: niveauAxe(axe, e.cle) }));
  const vues = etapes.filter((e) => e.n).length;
  return h`
  <div style="margin-top:12px">
    <div class="rang" style="gap:8px">
      <span class="pt sourd" style="letter-spacing:1.2px">LA CHAÎNE</span>
      <span class="pt pousse" id="compte-ia_chaine" style="text-align:right;${vues ? 'color:var(--copper)' : 'color:var(--basalt)'}">
        ${vues ? `${vues} étape${vues > 1 ? 's' : ''} sur ${etapes.length}` : 'aucune étape constatée'}</span>
    </div>
    <div class="chaine" style="margin-top:5px">
      ${etapes.map((e) => h`<div class="maillon ${e.n ? 'vu' : ''} aide"
          data-aide="${e.libelle} · ${e.aide}">
        <span class="noeud" aria-hidden="true"></span>
        <span class="sm pousse">${e.libelle}</span>${pavesAxe('ia_chaine', e.cle, e.n, id, ecriture)}
        <span class="pt niv">${e.n ? NIVEAUX_AXE[e.n] : 'non constaté'}</span></div>`)}
    </div>
  </div>`;
};

/** Les terrains sont des quantités comparables entre elles : une ligne chacun, triées. */
const terrainsIA = (axe: Axe | null, id: string, ecriture: boolean) => {
  // En lecture, on ne montre que ce qui est constaté : une liste de six zéros ne dit rien.
  // En écriture, on les montre tous — on ne peut pas régler un terrain qui n'est pas là,
  // et c'est précisément le terrain à zéro qu'un consultant vient corriger.
  const tous = TERRAINS_IA.map((t) => ({ ...t, n: niveauAxe(axe, t.cle) }));
  const n = tous.filter((t) => t.n).length;
  if (!n && !ecriture) return '';
  const vus = ecriture ? tous : [...tous].sort((a, b) => b.n - a.n).filter((t) => t.n);
  return h`<div style="margin-top:16px">
    <div class="rang" style="gap:8px">
      <span class="pt sourd" style="letter-spacing:1.2px">LES TERRAINS</span>
      <span class="pt pousse" id="compte-ia_terrains" style="text-align:right;${n ? 'color:var(--copper)' : 'color:var(--basalt)'}">
        ${n} sur ${TERRAINS_IA.length}</span>
    </div>
    ${vus.map((t) => h`<div class="rang axe aide ${t.n ? 'vu' : ''}" style="gap:9px;margin-top:6px"
        data-aide="${t.libelle} · ${t.aide}">
      <span class="sm pousse">${t.libelle}</span>${pavesAxe('ia_terrains', t.cle, t.n, id, ecriture)}
      <span class="pt niv">${t.n ? NIVEAUX_AXE[t.n] : 'non constaté'}</span></div>`)}
  </div>`;
};

/**
 * La cartographie IA, d'un seul tenant. Tout ce qui touche à l'IA vit ici : trois axes qui
 * ne disent pas la même chose — où, sur quoi, dans quel rôle — plus la profondeur et les
 * deux dates. Éparpillé, rien de tout cela ne se lit ; rassemblé, ça tient en un regard.
 */
const cartographieIA = (p: Record<string, unknown>, s: Synthese | null, id: string, ecriture: boolean) => {
  const niveau = p['ia_niveau'] as number | null;
  const annees = p['ia_annees'] as number | null;
  const postures = (p['ia_posture'] ?? []) as string[];
  const depuis = p['ia_depuis'] as number | null;
  const dernier = p['ia_dernier'] as number | null;
  const cetteAnnee = new Date().getFullYear();
  // Une pratique qui date est une pratique qui ne vaut plus ce qu'elle valait : le domaine
  // bouge trop vite pour qu'un silence de quatre ans soit neutre.
  const dort = dernier != null && cetteAnnee - dernier >= 3;

  return h`<section id="carto">
    <div class="filet-l"><span class="sur">CARTOGRAPHIE IA</span></div>

    <div style="margin-top:10px">
      <div class="rang"><span class="pt sourd" style="letter-spacing:1.2px">PROFONDEUR</span>
        <span class="pousse" style="font-size:13px;color:var(--copper)" id="ia-lu">
          ${niveau ? `${niveau} / 5 · ${NIVEAUX_IA[niveau]}` : 'non évaluée'}</span></div>
      ${ecriture ? h`<input type="range" id="ia-niveau" min="0" max="5" step="1"
          value="${niveau ?? 0}" data-id="${id}" style="width:100%;margin-top:6px"
          aria-label="Niveau d'IA perçu, de 1 à 5 ; zéro pour non évalué">` : ''}
    </div>

    ${chaineIA((p['ia_chaine'] ?? null) as Axe | null, id, ecriture)}
    ${terrainsIA((p['ia_terrains'] ?? null) as Axe | null, id, ecriture)}
    ${ecriture ? h`<p class="pt sourd" style="margin:7px 0 0;line-height:1.5">Cliquez un cran
      pour le poser, de nouveau pour l'effacer. Écrit aussitôt, sans refaire la synthèse.</p>` : ''}

    <div style="margin-top:14px">
      <div class="pt sourd" style="letter-spacing:1.2px">POSTURES</div>
      ${ecriture
        ? h`<div class="rang" style="flex-wrap:wrap;gap:5px;margin-top:7px" id="ia-postures">
            ${POSTURES_IA.map((x) => h`<button class="puce" data-posture="${x}" data-id="${id}"
              aria-pressed="${postures.includes(x)}">${x}</button>`)}</div>`
        : h`<div class="rang" style="flex-wrap:wrap;gap:5px;margin-top:7px">
            ${postures.length ? postures.map((x) => h`<span class="et o">${x}</span>`)
              : h`<span class="pt sourd">aucune établie</span>`}</div>`}
    </div>

    <div class="rang" style="gap:14px;margin-top:14px;flex-wrap:wrap;align-items:baseline">
      <div><span class="pt sourd" style="letter-spacing:1.2px">DEPUIS</span>
        ${ecriture
          ? h`<input class="champ" id="ia-depuis" type="number" min="1990" max="${cetteAnnee}"
                value="${depuis ?? ''}" data-id="${id}" placeholder="année"
                style="width:84px;height:26px;margin-left:7px">`
          : h`<span class="sm" style="margin-left:7px">${depuis ?? '—'}</span>`}
        ${annees != null ? h`<span class="pt" style="color:var(--copper);margin-left:5px">${annees} an${annees > 1 ? 's' : ''}</span>` : ''}</div>

      <div><span class="pt sourd" style="letter-spacing:1.2px">DERNIER PROJET</span>
        ${ecriture
          ? h`<input class="champ" id="ia-dernier" type="number" min="1990" max="${cetteAnnee}"
                value="${dernier ?? ''}" data-id="${id}" placeholder="année"
                style="width:84px;height:26px;margin-left:7px">`
          : h`<span class="sm" style="margin-left:7px">${dernier ?? '—'}</span>`}
        ${dort ? h`<span class="et a" style="margin-left:5px">pratique en sommeil</span>` : ''}</div>
    </div>

    ${s?.ia_justification ? h`<p class="pt sourd" style="margin:12px 0 0;line-height:1.55">
      ${s.ia_justification}</p>` : ''}
  </section>`;
};

/**
 * Ce que le CV WAKEA imprimera, et que rien d'autre dans MANA n'affiche. Un consultant
 * doit pouvoir vérifier d'un coup d'œil ce qui sortira du document avant de l'envoyer —
 * sans quoi il découvrirait une rubrique vide chez le client.
 */
const pourLeCv = (p: Record<string, unknown>, id: string, ecrire: boolean, cv: CvWakea) => {
  const depuis = p['experience_depuis'] as number | null;
  const annees = p['experience_annees'] as number | null;
  const langues = (p['langues'] ?? []) as { langue: string; niveau?: string | null }[];
  const formations = (p['formations'] ?? []) as { annee?: string | null; diplome: string; ecole?: string | null }[];
  const certifications = (p['certifications'] ?? []) as string[];
  const matiere = Boolean(depuis) || langues.length > 0 || formations.length > 0 || certifications.length > 0;

  // Les manques viennent du serveur, qui refuse pour de bon. Ils étaient recalculés ici,
  // et deux calculs de la même règle finissent toujours par se contredire — le jour où
  // l'un des deux change.
  const manques = cv.manques;

  /**
   * Trois états, et un bouton qui dit lequel.
   *
   * Un CV conservé n'a d'intérêt que si l'écran sait avant le clic ce qu'il va faire :
   * proposer « télécharger » et servir un document qui décrit la fiche d'il y a trois
   * semaines serait pire que de ne rien conserver du tout.
   */
  const mode = !cv.document ? 'produire' : cv.a_jour ? 'reprise' : 'refaire';
  const bouton = mode === 'reprise' ? 'Télécharger le CV WAKEA (PDF)'
    : mode === 'refaire' ? 'Refaire le CV WAKEA (PDF)' : 'Produire le CV WAKEA (PDF)';

  // Un lecteur ne voit ni les boutons ni le rappel : la section ne lui sert qu'à vérifier
  // ce qui s'imprimerait, et une section vide pour lui n'a rien à dire.
  if (!ecrire && !matiere) return '';

  return h`<section>
    <div class="filet-l"><span class="sur">LE CV WAKEA</span>
      <span class="pt sourd pousse" style="text-align:right">ce qui s'imprimera</span></div>
    ${ecrire ? h`<div style="margin-top:10px">
      <div class="rang" style="gap:7px;flex-wrap:wrap">
        <button class="bt s p" data-act="cv-wakea" data-id="${id}" data-mode="${mode}"
          ${manques.length ? 'disabled' : ''}>${bouton}</button>
        <button class="bt s" data-act="dossier" data-id="${id}">Dossier de présentation</button></div>
      ${mode === 'reprise' ? h`<p class="pt sourd" style="margin:7px 0 0;line-height:1.5">
          Produit le ${jour(cv.produit_le)} et conservé au dossier : il décrit la fiche telle
          qu'elle est, et se télécharge sans être refait.</p>`
        : mode === 'refaire' ? h`<p class="pt sourd" style="margin:7px 0 0;line-height:1.5">
            Le CV conservé a été produit le ${jour(cv.produit_le)}, <b>avant</b> une modification
            de la fiche. Il reste au dossier, en l'état ; le refaire le remplace.</p>`
        : ''}
      ${// Dit dans tous les cas où il y a quelque chose à dire, et pas seulement quand il
        // n'y a pas de CV : une fiche qui a perdu sa synthèse laisse un bouton « Refaire »
        // inerte, et un bouton qui ne réagit pas sans expliquer pourquoi est un défaut.
        manques.length ? h`<p class="pt sourd" style="margin:7px 0 0;line-height:1.5">Il manque
          ${manques.length > 1 ? `${manques.slice(0, -1).join(', ')} et ${manques[manques.length - 1]}` : manques[0]}
          pour composer le CV.</p>` : ''}
      <p class="pt sourd" style="margin:7px 0 0;line-height:1.5">Ni coordonnées, ni tarif, ni
         disponibilité, ni statut contractuel. Chaque sortie est inscrite au journal et comptée
         dans le seuil mensuel, reprise comprise.</p>
    </div>` : ''}
    ${depuis ? h`<div class="rang" style="margin-top:9px">
      <span class="pt sourd pousse" style="letter-spacing:1.2px">EXPÉRIENCE</span>
      <span class="sm">${annees} ans · depuis ${depuis}</span></div>` : ''}
    ${langues.length ? h`<div style="margin-top:9px">
      <div class="pt sourd" style="letter-spacing:1.2px">LANGUES</div>
      ${langues.map((l) => h`<div class="rang" style="margin-top:3px">
        <span class="sm pousse">${l.langue}</span>
        <span class="pt sourd">${l.niveau ?? ''}</span></div>`)}</div>` : ''}
    ${formations.length ? h`<div style="margin-top:11px">
      <div class="pt sourd" style="letter-spacing:1.2px">FORMATION</div>
      ${formations.map((f) => h`<div style="margin-top:4px">
        <div class="sm" style="color:var(--obsidian)">${f.diplome}</div>
        <div class="pt sourd">${[f.ecole, f.annee].filter(Boolean).join(' · ')}</div></div>`)}</div>` : ''}
    ${certifications.length ? h`<div style="margin-top:11px">
      <div class="pt sourd" style="letter-spacing:1.2px">CERTIFICATIONS</div>
      <ul style="margin:4px 0 0;padding-left:17px">${certifications.map((c) =>
        h`<li style="font-size:12px;line-height:1.5">${c}</li>`)}</ul></div>` : ''}
  </section>`;
};

const listeBloc = (titre: string, items: string[] | undefined) => items?.length
  ? h`<div style="margin-top:11px"><div class="pt sourd" style="letter-spacing:1.2px">${titre}</div>
      <ul style="margin:4px 0 0;padding-left:17px">${items.map((x) =>
        h`<li style="font-size:12.5px;line-height:1.55">${x}</li>`)}</ul></div>` : '';

export async function ecranProfil(id: string, session: Session, chrome: (corps: Brut) => Brut): Promise<void> {
  const d = await api.get<Dossier>(`/api/profils/${id}`);
  const p = d.profil;
  const synthese = (p['synthese'] ?? null) as Synthese | null;
  const qualifiable = peutEcrire(session) && p.statut !== 'qualifie';

  // Ce qui est arrivé au dossier depuis la dernière synthèse. Une synthèse n'est pas
  // fausse parce qu'un document est arrivé après elle — elle est incomplète, et c'est
  // au dossier de le dire plutôt qu'à quelqu'un de s'en souvenir.
  const syntheseLe = p['synthese_le'] ? new Date(String(p['synthese_le'])).getTime() : 0;
  const apres = (iso: string) => new Date(iso).getTime() > syntheseLe;
  const nouveaux = syntheseLe
    ? { documents: d.documents.filter((x) => apres(x.depose_le)).length,
        notes: d.notes.filter((x) => apres(x.cree_le)).length }
    : { documents: 0, notes: 0 };
  const depuis = [
    nouveaux.documents ? `${nouveaux.documents} document${nouveaux.documents > 1 ? 's' : ''}` : '',
    nouveaux.notes ? `${nouveaux.notes} note${nouveaux.notes > 1 ? 's' : ''}` : '',
  ].filter(Boolean).join(' et ');

  peindre('#app', chrome(h`
    <div class="page">
      <p class="sm sourd" style="margin:0"><button data-act="aller" data-vue="vivier" style="color:var(--copper)">Vivier</button> · ${p.prenom} ${p.nom}</p>

      <div class="entre" style="align-items:flex-start">
        <div>
          <div class="rang" style="flex-wrap:wrap;gap:10px">
            <h1 class="h1">${p.prenom} ${p.nom}</h1>
            ${p.role_libelle ? h`<span class="et">${p.role_libelle}</span>` : ''}
            <span class="et o">${p.types.join(' · ') || 'type non précisé'}</span>
            ${p.statut === 'qualifie' ? h`<span class="et v">Qualifié</span>` : h`<span class="et a">À qualifier</span>`}
            ${p['proprietaire'] === 'SITE'
              ? h`<span class="et c">Entrée du site · à prendre en main</span>`
              : p['proprietaire'] ? h`<span class="et o">Relation portée par ${String(p['proprietaire'])}</span>` : ''}
            ${p['a_prefiller'] ? h`<span class="et c">CV à lire</span>` : ''}
            ${p['a_resynthetiser'] ? h`<span class="et c">Synthèse à refaire</span>` : ''}
            ${p['origine'] ? h`<span class="et o">via ${String(p['origine'])}</span>` : ''}
          </div>
          <div class="rang" style="flex-wrap:wrap;gap:22px;margin-top:7px">
            <span class="sm">Disponible : ${p.dispo_texte ?? '—'}
              ${p['disponible_a_partir_de'] ? h`<span class="pt" style="color:var(--copper)">· libre le ${jour(String(p['disponible_a_partir_de']))}</span>` : ''}
              ${p['dispo_le'] ? h`<span class="pt sourd">· dit le ${jour(String(p['dispo_le']))}</span>` : ''}</span>
            <span class="sm">${condition(p)}
              ${equivalentJour(p) ? h`<span class="pt sourd">${equivalentJour(p)}</span>` : ''}</span>
            <span class="sm">${p.zones.join(', ') || '—'}</span>
          </div>
          ${coordonnees(p)}
        </div>
        ${peutEcrire(session) ? h`<div class="rang" style="gap:7px">
          <button class="bt" data-act="modifier" data-id="${id}">Modifier la fiche</button>
          ${p['proprietaire'] === 'SITE'
            ? h`<button class="bt c" data-act="prendre" data-id="${id}">Prendre en main</button>` : ''}
          ${qualifiable ? h`<button class="bt c" data-act="qualifier" data-id="${id}">Qualifier ce profil</button>` : ''}
        </div>` : ''}
      </div>

      <div style="display:flex;gap:26px;align-items:flex-start;flex-wrap:wrap">
        <div style="flex:1 1 440px;min-width:0;display:flex;flex-direction:column;gap:18px">
          ${p['a_prefiller'] && peutEcrire(session) ? (() => {
            const cv = d.documents.find((x) => x.categorie === 'cv');
            return cv ? h`<div class="enc ar">
              <div class="rang"><span class="sur">UN CV ATTEND D'ÊTRE LU</span>
                <span class="pt sourd pousse">${cv.nom}</span></div>
              <p>${p['cv_lu_le']
                ? 'Un CV plus récent est arrivé depuis la dernière lecture.'
                : "Ce dossier porte un CV que personne n'a encore fait lire."}
                 Le modèle en <b>proposera</b> une fiche ; rien n'entrera au dossier avant que vous
                 ne l'ayez relue, champ par champ.</p>
              <div class="rang" style="margin-top:9px">
                <button class="bt c" data-act="prefill" data-id="${id}"
                  data-doc="${cv.id}" data-nom="${cv.nom}">Lire le CV et pré-remplir</button></div></div>` : '';
          })() : ''}

          ${p['message'] ? h`<div class="enc gl">
              <span class="sur">CE QUE LA PERSONNE A ÉCRIT EN SE PRÉSENTANT</span>
              <p style="white-space:pre-wrap">${String(p['message'])}</p>
              <p class="pt sourd" style="margin-top:7px">Ses mots, déposés sur le formulaire du site.
                 Ni l'équipe ni un modèle ne les modifient.</p></div>` : ''}

          ${synthese ? h`<div class="enc st">
              <div class="rang"><span class="sur">SYNTHÈSE · RELUE ET VALIDÉE</span>
                <span class="pt sourd pousse">${instant(String(p['synthese_le'] ?? ''))}</span>
                ${peutEcrire(session) ? h`<button class="bt s" data-act="synthese" data-id="${id}">
                  Refaire la synthèse</button>` : ''}</div>
              ${depuis ? h`<div class="rang" style="gap:7px;margin-top:8px">
                <span class="et a">${depuis} depuis</span>
                <span class="pt sourd">la synthèse ne les a pas lus</span></div>` : ''}
              ${p['a_resynthetiser'] ? h`<div class="rang" style="gap:7px;margin-top:8px">
                <span class="et c">Consigne antérieure</span>
                <span class="pt sourd">cette synthèse ne porte ni positionnement ni niveau d'IA
                  — la refaire les ajoutera</span></div>` : ''}
              <div style="font-size:13px;line-height:1.55;margin-top:8px">${synthese.positionnement}</div>
              ${synthese.prouve.length ? h`<div style="margin-top:11px">
                <div class="pt sourd" style="letter-spacing:1.2px">SAVOIR-FAIRE PROUVÉ</div>
                ${synthese.prouve.map((f) => h`<div style="margin-top:5px;font-size:12.5px;line-height:1.55">
                  ${f.fait} <span class="pt" style="color:var(--copper)">— ${f.source}</span></div>`)}</div>` : ''}
              ${listeBloc('POINTS DE VIGILANCE', synthese.vigilance)}
              ${listeBloc('QUESTIONS OUVERTES', synthese.ouvertes)}</div>`
            : h`<div class="enc st"><div class="rang"><span class="sur">PAS ENCORE DE SYNTHÈSE</span>
                  ${peutEcrire(session) ? h`<button class="bt s pousse" data-act="synthese" data-id="${id}">Produire la synthèse</button>` : ''}</div>
                <p>Elle se produit à partir de la fiche, des expériences, des notes de l'équipe et du
                   compte rendu d'entretien. Elle est proposée, jamais écrite sans votre relecture.</p></div>`}

          <section>
            <div class="filet"><span class="sur">NOTES DE L'ÉQUIPE</span>
              <span class="pt sourd">${d.notes.length} · non modifiables une fois enregistrées</span></div>
            ${peutEcrire(session) ? h`
              <form id="note" style="margin-top:11px;display:flex;flex-direction:column;gap:7px">
                <textarea class="champ" id="note-texte" rows="2" maxlength="4000"
                  placeholder="Ce que vous voulez que l'équipe sache…"></textarea>
                <div class="rang">
                  ${estAdmin(session) ? h`<label class="rang sm" style="gap:6px">
                    <input type="checkbox" id="note-privee"> Réservée aux associés</label>` : ''}
                  <span class="pousse"></span>
                  <button class="bt s p" type="submit">Enregistrer la note</button></div></form>` : ''}
            <div style="margin-top:13px;display:flex;flex-direction:column;gap:10px">
              ${d.notes.length ? d.notes.map((n) => h`
                <div class="rang" style="align-items:flex-start;gap:12px">
                  <div style="width:84px;flex-shrink:0">
                    <div style="font-size:11px;color:var(--obsidian)">${n.auteur}</div>
                    <div class="pt sourd">${instant(n.cree_le)}</div>
                    ${n.visibilite === 'associes' ? h`<div class="pt" style="color:var(--copper)">associés</div>` : ''}</div>
                  <div class="pousse" style="border-left:1px solid ${n.visibilite === 'associes' ? 'var(--copper)' : 'var(--hair)'};
                       padding-left:12px;font-size:12.5px;line-height:1.55">${n.texte}</div></div>`)
                : h`<p class="sm sourd">Aucune note pour l'instant.</p>`}</div>
          </section>

          ${d.experiences.length ? h`<section>
            <div class="filet"><span class="sur">EXPÉRIENCES</span></div>
            ${d.experiences.map((x) => h`<div class="carte" style="margin-top:9px">
              <div style="font-size:12.5px;color:var(--obsidian)">${x.intitule}${x.client ? ` · ${x.client}` : ''}</div>
              <div class="sm" style="margin-top:4px;line-height:1.5">${x.resume}</div></div>`)}</section>` : ''}
        </div>

        <div style="flex:0 1 350px;min-width:270px;display:flex;flex-direction:column;gap:16px">
          ${cartographieIA(p, synthese, id, peutEcrire(session))}
          ${positionnement(synthese)}
          ${pourLeCv(p, id, peutEcrire(session), d.cv_wakea)}

          ${p['proprietaire'] === 'SITE' && peutEcrire(session) ? h`<div class="enc c">
            <span class="sur">ENTRÉE DU SITE · PERSONNE NE LA PORTE</span>
            <p>Cette personne a candidaté d'elle-même et attend dans la file des entrées. La
               prendre en main la met à votre nom : elle quitte la file, et quelqu'un répond
               désormais d'elle. Nous lui avons dit que nous regarderions son dossier.</p>
            <button class="bt p" data-act="prendre" data-id="${id}"
              style="align-self:flex-start;margin-top:9px">La prendre en main</button></div>` : ''}

          ${p.statut !== 'qualifie' ? h`<div class="enc ar"><span class="sur">PROFIL À QUALIFIER</span>
            <p>Le passage à l'état qualifié exige un compte rendu ou une retranscription d'entretien au dossier.</p></div>` : ''}

          <section class="bloc-doc">
            <div class="filet-l"><span class="sur">DOCUMENTS · ${d.documents.length}</span>
              <span class="pt sourd pousse" style="text-align:right">dépôt et consultation</span></div>
            ${peutEcrire(session) ? h`
              <form id="depot" style="margin-top:10px;display:flex;flex-direction:column;gap:7px">
                <select class="champ" id="categorie">
                  ${Object.entries(CATEGORIES).map(([v, l]) => h`<option value="${v}">${l}</option>`)}</select>
                <input class="champ" type="file" id="fichier" accept=".pdf,.docx,.doc,.png,.jpg,.jpeg,.txt,.md" required>
                <p class="pt sourd" style="margin:0">Un <b>CV</b> ou un <b>entretien</b> doit être
                   lisible — PDF, .docx, .txt ou .md : ce sont les pièces que le modèle lit et que
                   la recherche indexe. Une référence, un contrat ou une autre pièce acceptent
                   aussi .doc, .png et .jpg.</p>
                <button class="bt p" type="submit" style="align-self:flex-start">Déposer le document</button></form>` : ''}
            <div style="margin-top:10px;display:flex;flex-direction:column;gap:10px">
              ${d.documents.map((x) => h`<div class="carte">
                <div style="font-size:12.5px;color:var(--obsidian)">${x.nom}</div>
                <div class="pt sourd">${LIBELLES[x.categorie] ?? x.categorie} · ${jour(x.depose_le)}
                  ${x.depose_par ? `· ${x.depose_par}` : ''} · ${Math.round(x.taille / 1024)} ko
                  ${x.origine === 'genere' ? '· produit par MANA' : ''}</div>
                ${x.categorie === 'cv_wakea' && !d.cv_wakea.a_jour
                  // Le dire là aussi, et pas seulement dans la section du CV : c'est d'ici
                  // qu'on télécharge sans réfléchir, et un document périmé qui ne
                  // s'annonce pas est exactement ce qu'on cherchait à éviter en le gardant.
                  ? h`<div class="pt" style="margin-top:4px;color:var(--ambre)">La fiche a changé
                      depuis : ce PDF la décrit telle qu'elle était.</div>` : ''}
                <div class="rang" style="gap:7px;margin-top:8px">
                  <button class="bt s" style="border-color:var(--obsidian)" data-act="voir" data-id="${x.id}" data-nom="${x.nom}">Voir</button>
                  ${session.role === 'lecture' ? '' :
                    h`<button class="bt s" data-act="telecharger" data-id="${x.id}">Télécharger</button>`}
                  <span class="pousse"></span>
                  <span class="pt sourd">${x.consultations} ouv. · ${x.telechargements} téléch.</span></div></div>`)}
            </div>
          </section>

          ${conformite(p, id, peutEcrire(session))}
        </div>
      </div>
    </div>`));

  // Le curseur et l'année : écrits à la volée, sans redessiner la fiche. Une correction
  // qui exigerait de rouvrir un formulaire ne serait pas faite, et le chiffre resterait faux.
  const poser = async (champ: 'ia_niveau' | 'ia_depuis' | 'ia_dernier', valeur: number | null) => {
    try { await api.patch(`/api/profils/${id}`, { [champ]: valeur }); }
    catch (e) { mot(direRefus(e)); }
  };

  /**
   * Le réglage d'un cran. L'écran est peint d'abord, l'écriture suit : un aller-retour
   * réseau à chaque clic rendrait le réglage poussif, et un consultant qui corrige six
   * cases ne doit pas attendre six fois. En cas de refus, l'état d'avant est remis et le
   * refus se dit — mentir sur un enregistrement serait pire que de le refuser.
   */
  const axes: Record<'ia_chaine' | 'ia_terrains', Record<string, number>> = {
    ia_chaine: { ...((p['ia_chaine'] ?? {}) as Record<string, number>) },
    ia_terrains: { ...((p['ia_terrains'] ?? {}) as Record<string, number>) },
  };
  const TOTAL = { ia_chaine: ETAPES_CHAINE.length, ia_terrains: TERRAINS_IA.length };

  const peindreAxe = (axe: 'ia_chaine' | 'ia_terrains', cle: string, n: number) => {
    const groupe = $(`.crans[data-axe="${axe}"][data-cle="${cle}"]`);
    const ligne = groupe?.closest<HTMLElement>('.maillon, .axe');
    groupe?.querySelectorAll<HTMLElement>('.cran').forEach((bouton, i) => {
      bouton.setAttribute('aria-pressed', String(i < n));
      bouton.title = `${NIVEAUX_AXE[i + 1]}${i + 1 === n ? ' — cliquer de nouveau pour effacer' : ''}`;
      bouton.querySelector('i')?.classList.toggle('plein', i < n);
    });
    ligne?.classList.toggle('vu', n > 0);
    const niv = ligne?.querySelector('.niv');
    if (niv) niv.textContent = n ? NIVEAUX_AXE[n] ?? '' : 'non constaté';

    const compte = $(`#compte-${axe}`);
    if (!compte) return;
    const vus = Object.values(axes[axe]).filter((x) => x > 0).length;
    compte.textContent = axe === 'ia_terrains'
      ? `${vus} sur ${TOTAL[axe]}`
      : vus ? `${vus} étape${vus > 1 ? 's' : ''} sur ${TOTAL[axe]}` : 'aucune étape constatée';
    compte.style.color = vus ? 'var(--copper)' : 'var(--basalt)';
  };

  $('#carto')?.addEventListener('click', (ev) => {
    const bouton = (ev.target as HTMLElement | null)?.closest<HTMLElement>('.cran');
    if (!bouton) return;
    const axe = bouton.dataset['axe'] as 'ia_chaine' | 'ia_terrains' | undefined;
    const cle = bouton.dataset['cle'];
    const cran = Number(bouton.dataset['cran']);
    if (!axe || !cle || !Number.isInteger(cran)) return;

    const avant = { ...axes[axe] };
    // Recliquer le cran courant efface : c'est la seule manière de redescendre à zéro.
    const vise = axes[axe][cle] === cran ? 0 : cran;
    if (vise) axes[axe][cle] = vise; else delete axes[axe][cle];
    peindreAxe(axe, cle, vise);

    void (async () => {
      try { await api.patch(`/api/profils/${id}`, { [axe]: axes[axe] }); }
      catch (e) {
        axes[axe] = avant;
        peindreAxe(axe, cle, avant[cle] ?? 0);
        mot(direRefus(e));
      }
    })();
  });

  const curseur = $<HTMLInputElement>('#ia-niveau');
  curseur?.addEventListener('input', () => {
    const n = Number(curseur.value);
    const lu = $('#ia-lu');
    if (lu) lu.textContent = n ? `${n} / 5 · ${NIVEAUX_IA[n]}` : 'non évalué';
  });
  curseur?.addEventListener('change', () => { void poser('ia_niveau', Number(curseur.value) || null); });

  for (const [sel, champ] of [['#ia-depuis', 'ia_depuis'], ['#ia-dernier', 'ia_dernier']] as const) {
    const champAnnee = $<HTMLInputElement>(sel);
    champAnnee?.addEventListener('change', () => {
      const v = Number(champAnnee.value);
      void poser(champ, Number.isFinite(v) && v >= 1990 ? v : null);
    });
  }

  // Les postures : plusieurs peuvent être vraies à la fois, donc on bascule chaque puce
  // plutôt que d'en choisir une. L'état vit dans la page le temps du clic, et part en base.
  $('#ia-postures')?.addEventListener('click', (ev) => {
    const puce = (ev.target as HTMLElement | null)?.closest<HTMLElement>('[data-posture]');
    if (!puce) return;
    puce.setAttribute('aria-pressed', puce.getAttribute('aria-pressed') === 'true' ? 'false' : 'true');
    const choisies = [...document.querySelectorAll<HTMLElement>('[data-posture][aria-pressed="true"]')]
      .map((x) => x.dataset['posture']!);
    void api.patch(`/api/profils/${id}`, { ia_posture: choisies }).catch((e) => mot(direRefus(e)));
  });

  $('#note')?.addEventListener('submit', async (ev) => {
    ev.preventDefault();
    const texte = valeur('#note-texte');
    if (!texte) return mot('La note est vide.');
    try {
      await api.post(`/api/profils/${id}/notes`, {
        texte, visibilite: ($('#note-privee') as HTMLInputElement | null)?.checked ? 'associes' : 'equipe',
      });
      mot('Note enregistrée.');
      await ecranProfil(id, session, chrome);
    } catch (e) { mot(direRefus(e)); }
  });

  $('#depot')?.addEventListener('submit', async (ev) => {
    ev.preventDefault();
    const entree = $('#fichier') as HTMLInputElement | null;
    const fichier = entree?.files?.[0];
    if (!fichier) return mot('Choisissez un fichier.');
    const forme = new FormData();
    forme.set('categorie', valeur('#categorie') || 'autre');
    forme.set('fichier', fichier);
    try {
      await api.post(`/api/profils/${id}/documents`, forme);
      mot('Document déposé.');
      await ecranProfil(id, session, chrome);
    } catch (e) { mot(direRefus(e)); }
  });
}

/** Visionneuse : le document reste dans l'application, dans un cadre isolé. */
/** Ce qu'un navigateur sait rendre dans un cadre. Le reste se lit en texte. */
const AFFICHABLES = ['pdf', 'png', 'jpg', 'jpeg', 'txt', 'md'];
const seVoit = (nom: string) => AFFICHABLES.includes(nom.toLowerCase().split('.').pop() ?? '');

export function couchDocument(documentId: string, nom: string): Brut {
  const cadre = seVoit(nom);
  return h`<div class="voile" data-act="fermer-voile">
    <div class="couche large-c">
      <div class="ct"><div><div class="sur">DOCUMENT</div><h2>${nom}</h2></div>
        <button class="x" data-act="fermer" aria-label="Fermer">✕</button></div>
      <div class="cc">${cadre
        ? h`<iframe class="visio" src="/api/documents/${documentId}/contenu?mode=voir"
              title="${nom}" sandbox></iframe>`
        : h`<div class="enc gl"><span class="sur">CE FORMAT NE S'AFFICHE PAS DANS UN NAVIGATEUR</span>
              <p>Word ne se rend pas dans une page. Voici le texte du document, sans sa mise en
                 forme — assez pour vérifier une date ou relire un passage, sans sortir le fichier.</p></div>
            <div class="texte-doc" id="texte-doc"><p class="md-p sourd">Lecture…</p></div>`}</div>
      <div class="cp"><span class="sm sourd pousse">Lecture : aucune trace de téléchargement.</span>
        <button class="bt" data-act="fermer">Fermer</button>
        <button class="bt c" data-act="telecharger" data-id="${documentId}">Télécharger</button></div>
    </div></div>`;
}

/** Va chercher le texte une fois la couche posée. Appelée par l'aiguillage. */
export async function remplirTexteDocument(documentId: string): Promise<void> {
  const cible = $('#texte-doc');
  if (!cible) return;
  try {
    const r = await api.get<{ texte: string; retires: number }>(
      `/api/documents/${documentId}/contenu?mode=texte`);
    // Le nettoyage s'annonce. Alléger en silence ferait douter de ce qu'on lit, et c'est
    // le dernier endroit où il faut douter : le fichier d'origine, lui, n'a pas bougé.
    peindre('#texte-doc', h`${r.retires ? h`<p class="pt sourd" style="margin:0 0 10px">
        ${r.retires} ligne${r.retires > 1 ? 's' : ''} d'horodatage et de répétition retirée${r.retires > 1 ? 's' : ''}
        pour la lecture — le fichier déposé est intact.</p>` : ''}
      ${rendreMarkdown(r.texte)}`);
  } catch (e) {
    cible.textContent = direRefus(e);
  }
}
